// Live subdomain availability for the wizard.
//
// Format is checked locally at once; availability goes to
// GET /platform/leagues/check-subdomain ~350ms after typing stops (one
// request per pause, stale answers ignored). If the check itself fails, you
// can still continue: the server checks again on create.

import { useEffect, useState } from 'react'
import { api } from '@/api'
import { errorMessage } from '@/api/client'
import { SUBDOMAIN_PATTERN } from './draft'

export type SubdomainCheckState =
  | { status: 'empty' }
  | { status: 'invalid'; message: string }
  | { status: 'checking' }
  | { status: 'available' }
  | { status: 'unavailable'; reason: 'invalid' | 'reserved' | 'taken'; message: string }
  | { status: 'error'; message: string }

export const CHECK_DELAY_MS = 350

const FORMAT_MESSAGE = 'Use lowercase letters, numbers and hyphens (up to 63), not starting or ending with a hyphen.'

export function useSubdomainCheck(subdomain: string): SubdomainCheckState {
  const [state, setState] = useState<SubdomainCheckState>({ status: 'empty' })

  useEffect(() => {
    if (!subdomain) {
      setState({ status: 'empty' })
      return
    }
    if (!SUBDOMAIN_PATTERN.test(subdomain)) {
      setState({ status: 'invalid', message: FORMAT_MESSAGE })
      return
    }
    setState({ status: 'checking' })
    const controller = new AbortController()
    const t = setTimeout(() => {
      api.tenants
        .checkSubdomain(subdomain, controller.signal)
        .then((r) => {
          if (controller.signal.aborted) return
          if (r.available) setState({ status: 'available' })
          else setState({ status: 'unavailable', reason: r.reason ?? 'invalid', message: r.message ?? `${subdomain} can’t be used.` })
        })
        .catch((err) => {
          if (controller.signal.aborted) return
          setState({ status: 'error', message: `Couldn't check availability (${errorMessage(err)}). You can continue; the server checks again when you create.` })
        })
    }, CHECK_DELAY_MS)
    return () => {
      clearTimeout(t)
      controller.abort()
    }
  }, [subdomain])

  return state
}

/** May the person continue past step 1 with this result? */
export function subdomainOk(s: SubdomainCheckState): boolean {
  return s.status === 'available' || s.status === 'error'
}
